import {
  DefaultAlertDialog,
  DefaultModalDialog,
  Image,
  LabeledText,
  useToast,
} from '~/common/components/defaultUIComponents';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Spinner,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import RoutineRecordApi, {
  ModifiedRoutineRecord,
} from '~/common/api/zeiterfassungServerApi/RoutineRecordApi';
import RecordPictureApi from '~/common/api/zeiterfassungServerApi/RecordPicApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { DeleteIcon, DownloadIcon, ViewIcon } from '@chakra-ui/icons';
import { formatName } from '~/common/utils/employeeUtils';
import useTranslate from '~/common/utils/translate';
import { Paper } from '~/common/components/Paper';
import React, { useMemo } from 'react';
import { format } from 'date-fns';

type Props = {
  record: ModifiedRoutineRecord;
};

export const RoutineRecordListItem: React.FC<Props> = ({ record }) => {
  const { isOpen, onToggle } = useDisclosure();
  const { isOpen: deleteIsOpen, onToggle: deleteOnToggle } = useDisclosure();
  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const employee = useMemo(() => {
    return employees?.find((e) => parseInt(e.id) === record.employee);
  }, [employees]);
  const { data: pictures, isLoading } =
    RecordPictureApi.hooks.usePicturesOfRoutineRecord(record, {});
  const { mutateAsync: deleteRoutine, isLoading: isDeleting } =
    RoutineRecordApi.hooks.useDeleteRoutineWithPicture(record);

  const onDownload = (src: string) => {
    const link = document.createElement('a');
    link.download = `Routine_${record.routine.name}`;
    link.href = src;
    link.click();
    document.body.removeChild(link);
  };

  const toast = useToast({});
  const translate = useTranslate();

  function formatDuration(durationInSeconds: number) {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = durationInSeconds % 60;

    const formattedDuration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    return formattedDuration;
  }

  return (
    <>
      <Paper w="full" h="full" px={3} py={2}>
        <DefaultAlertDialog
          dialogHeaderText={
            'dashboard.routineRecord.individualDelete.confirmation.header'
          }
          dialogBodyText={
            'dashboard.routineRecord.individualDelete.confirmation'
          }
          isOpen={deleteIsOpen}
          onConfirmedAction={() =>
            void deleteRoutine().catch(() =>
              toast({ title: 'generic.error', status: 'error' }),
            )
          }
          onCancel={deleteOnToggle}
          isLoading={isDeleting}
        />
        <Grid templateColumns="repeat(3, 1fr)">
          <GridItem colSpan={2}>
            <LabeledText label="dashboard.routineRecord.routine">
              {record.routine.name}
            </LabeledText>
          </GridItem>
          <GridItem
            top={0}
            right={0}
            display="flex"
            justifyContent="end"
            gap={3}
          >
            <Tooltip
              label={
                pictures && pictures.length > 0
                  ? translate('dashboard.routineRecord.showImages')
                  : translate('dashboard.routineRecord.noImages')
              }
              placement="top"
            >
              <Box
                cursor={
                  pictures && pictures.length > 0 ? 'pointer' : 'not-allowed'
                }
                onClick={pictures && pictures.length > 0 ? onToggle : undefined}
              >
                <ViewIcon
                  color={
                    pictures && pictures.length > 0 ? 'green.600' : 'gray.600'
                  }
                />
              </Box>
            </Tooltip>
            <Box _hover={{ cursor: 'pointer' }} onClick={deleteOnToggle}>
              <DeleteIcon color="red.600" />
            </Box>
          </GridItem>
          <GridItem>
            <LabeledText
              label="dashboard.routineRecord.predictedTime"
              col={true}
            >
              {record.routine.duration}
            </LabeledText>
          </GridItem>
          <GridItem />
          <GridItem>
            <LabeledText
              label="dashboard.routineRecord.measuredTime"
              col={true}
            >
              {formatDuration(Math.round((record.end - record.start) / 1000))}
            </LabeledText>
          </GridItem>
          <GridItem>
            <LabeledText label="dashboard.routineRecord.start" col={true}>
              {format(record.start, 'HH:mm dd.MM.yyyy')}
            </LabeledText>
          </GridItem>
          <GridItem />
          <GridItem>
            <LabeledText label="dashboard.routineRecord.end" col={true}>
              {format(record.end, 'HH:mm dd.MM.yyyy')}
            </LabeledText>
          </GridItem>
          <GridItem colSpan={3}>
            <LabeledText label="dashboard.routineRecord.doneAmount">
              {record.amount}
            </LabeledText>
          </GridItem>
          <GridItem colSpan={3}>
            <LabeledText label="dashboard.newtext.employee">
              {formatName(employee)}
            </LabeledText>
          </GridItem>
          <GridItem colSpan={3}>
            <LabeledText label="dashboard.routineRecord.description">
              {record.routine.description}
            </LabeledText>
          </GridItem>
          {record.comment && (
            <GridItem colSpan={3}>
              <LabeledText label="dashboard.routineRecord.comment">
                {record.comment}
              </LabeledText>
            </GridItem>
          )}
        </Grid>
      </Paper>
      <DefaultModalDialog
        dialogHeaderText="dashboard.routinePictures.modalHeader"
        headerParams={[record.routine.name]}
        submitText="OK"
        isOpen={isOpen}
        showCancelButton={false}
        onCancel={onToggle}
        onSubmit={onToggle}
        initialValues={{}}
      >
        <Flex
          direction="column"
          maxH="50vh"
          overflowY="auto"
          alignItems="center"
        >
          {isLoading && <Spinner />}
          {isOpen &&
            pictures?.map((pic) => (
              <Flex direction="column" key={pic.id}>
                <Image src={pic.picture} />
                <IconButton
                  aria-label="downlad"
                  icon={<DownloadIcon />}
                  onClick={() => onDownload(pic.picture)}
                />
              </Flex>
            ))}
        </Flex>
      </DefaultModalDialog>
    </>
  );
};
